import { If, type JSX } from 'cdui-js';
import './difi-skeleton.css';

export type DiFiSkeletonAvatarShape = 'round' | 'square';
export type DiFiSkeletonImageShape = 'round' | 'square';

const DEFAULT_ROW_WIDTH = '100%';
const DEFAULT_LAST_ROW_WIDTH = '60%';

const toCssSize = (v?: number | string) => {
  if (v == null || v === '') return undefined;
  return typeof v === 'number' ? `${v}px` : v;
};

export type DiFiSkeletonParagraphProps = {
  class?: string;
  round?: boolean;
  rowWidth?: number | string;
  style?: JSX.CSSProperties;
};

/** 段落占位（对齐 van-skeleton-paragraph） */
export const DiFiSkeletonParagraph = (props: DiFiSkeletonParagraphProps) => (
  <div
    class={`difi-skeleton-paragraph${props.round ? ' difi-skeleton-paragraph-round' : ''} ${props.class ?? ''}`.trim()}
    style={{ width: toCssSize(props.rowWidth) ?? '100%', ...props.style }}
  />
);

export type DiFiSkeletonTitleProps = {
  class?: string;
  round?: boolean;
  titleWidth?: number | string;
  style?: JSX.CSSProperties;
};

/** 标题占位（对齐 van-skeleton-title） */
export const DiFiSkeletonTitle = (props: DiFiSkeletonTitleProps) => (
  <div
    class={`difi-skeleton-title${props.round ? ' difi-skeleton-title-round' : ''} ${props.class ?? ''}`.trim()}
    style={{ width: toCssSize(props.titleWidth) ?? '40%', ...props.style }}
  />
);

export type DiFiSkeletonAvatarProps = {
  class?: string;
  avatarSize?: number | string;
  avatarShape?: DiFiSkeletonAvatarShape;
  style?: JSX.CSSProperties;
};

/** 头像占位（对齐 van-skeleton-avatar） */
export const DiFiSkeletonAvatar = (props: DiFiSkeletonAvatarProps) => {
  const size = toCssSize(props.avatarSize) ?? '32px';
  const shape = props.avatarShape ?? 'round';
  return (
    <div
      class={`difi-skeleton-avatar difi-skeleton-avatar-${shape} ${props.class ?? ''}`.trim()}
      style={{ width: size, height: size, ...props.style }}
    />
  );
};

export type DiFiSkeletonImageProps = {
  class?: string;
  imageSize?: number | string;
  imageShape?: DiFiSkeletonImageShape;
  style?: JSX.CSSProperties;
};

/** 图片占位（对齐 van-skeleton-image） */
export const DiFiSkeletonImage = (props: DiFiSkeletonImageProps) => {
  const size = toCssSize(props.imageSize) ?? '96px';
  const shape = props.imageShape ?? 'square';
  return (
    <div
      class={`difi-skeleton-image difi-skeleton-image-${shape} ${props.class ?? ''}`.trim()}
      style={{ width: size, height: size, ...props.style }}
    />
  );
};

export type DiFiSkeletonProps = {
  class?: string;
  style?: JSX.CSSProperties;
  /** 段落占位行数 */
  row?: number | string;
  /** 段落宽度；可传数组分别设置每一行；未传时末行默认 60% */
  rowWidth?: number | string | (number | string)[];
  /** 是否显示标题占位 */
  title?: boolean;
  /** 是否显示头像占位 */
  avatar?: boolean;
  /** 是否显示骨架；false 时渲染 children */
  loading?: boolean;
  /** 是否开启动画，默认 true */
  animate?: boolean;
  /** 标题/段落圆角风格 */
  round?: boolean;
  titleWidth?: number | string;
  avatarSize?: number | string;
  avatarShape?: DiFiSkeletonAvatarShape;
  /** loading=false 时展示的真实内容 */
  children?: JSX.Element;
  /** 自定义骨架（对齐 Vant #template） */
  template?: JSX.Element;
  templateRender?: () => JSX.Element;
};

const SkeletonPlaceholder = (props: DiFiSkeletonProps) => {
  const animate = props.animate !== false;
  const rowCount = Number(props.row ?? 0) || 0;

  const rowWidthAt = (index: number) => {
    const rw = props.rowWidth;
    if (Array.isArray(rw)) return rw[index] ?? DEFAULT_ROW_WIDTH;
    if (rw != null) return rw;
    return index === rowCount - 1 ? DEFAULT_LAST_ROW_WIDTH : DEFAULT_ROW_WIDTH;
  };

  const custom = props.templateRender ? props.templateRender() : props.template;

  return (
    <div
      class={`difi-skeleton${animate ? ' difi-skeleton-animate' : ''} ${props.class ?? ''}`.trim()}
      style={props.style}
      aria-busy="true"
      aria-live="polite"
    >
      {custom ?? (
        <>
          {props.avatar ? (
            <DiFiSkeletonAvatar avatarSize={props.avatarSize} avatarShape={props.avatarShape} />
          ) : null}
          <div class="difi-skeleton-content">
            {props.title ? (
              <DiFiSkeletonTitle round={props.round} titleWidth={props.titleWidth} />
            ) : null}
            {Array.from({ length: rowCount }, (_, i) => (
              <DiFiSkeletonParagraph round={props.round} rowWidth={rowWidthAt(i)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * 骨架屏（对齐 Vant 4 Skeleton API，cdui-js 版，无 vant 依赖）
 * loading 切换必须走 <If>：cdui 组件函数只执行一次，顶层 early return 不会随 props 更新。
 * @see https://vant-ui.github.io/vant/#/zh-CN/skeleton
 */
export const DiFiSkeleton = (props: DiFiSkeletonProps) => (
  <>
    <If when={props.loading !== false}>
      <SkeletonPlaceholder {...props} />
    </If>
    <If when={props.loading === false}>{props.children}</If>
  </>
);
